import z from "zod";
import { AmenityBookingSchema } from "./AmenityBooking";
import { CommentSchema } from "./Comment";
import { LocationSchema } from "./Location";
import { MemberSchema } from "./Member";
import { MembershipType } from "./MembershipType";
import { RecurrenceSchema } from "./Recurrence";
import { RoomSchema } from "./Room";

export const BookingSchema = z.object({
  id: z.string(),
  shortCode: z.string().optional(),
  locationId: z.string(),
  roomId: z.string(),
  memberId: z.string(),
  parentBookingId: z.string().optional(),
  // Set on the NEW series created when a recurring flex booking's time/date is changed and the
  // series is split at the edit point — points at the (now bounded) original series. Traceability:
  // the original runs to its `until`, this series carries the new time from the split forward.
  splitFromBookingId: z.string().optional(),
  recurrence: RecurrenceSchema.optional(),
  recurrencePattern: RecurrenceSchema.optional(), // Stores original recurrence for expanded bookings
  location: LocationSchema.partial().optional(),
  room: RoomSchema.partial().optional(),
  member: MemberSchema.partial().optional(),
  date: z.coerce.date(), // ISO date string (YYYY-MM-DD) or Date object
  startTime: z.string(), // Time in HHmm format (e.g., "0900", "1430")
  endTime: z.string(), // Time in HHmm format (e.g., "0930", "1600")
  type: z.enum(MembershipType).catch("prospect"), // Default to "prospect" if invalid type
  title: z.string().optional(),
  memberNotes: z.string().optional(),
  isCancelled: z.boolean().optional(),
  // R16 (hearth/docs/BOOKING_RULES.md#r16) — set to true when a booking is cancelled within 24h
  // of any occurrence. Billing uses this to charge the member despite the cancellation.
  wasChargedOnCancel: z.boolean().optional(),
  // Set when the booking is amended within 24h of any occurrence — change is applied
  // AND billing honours the original commitment so members cannot shorten/move to dodge
  // the original charge.
  wasChargedOnAmendment: z.boolean().optional(),
  isSystemBooking: z.boolean().optional(),
  hasBuffers: z.boolean().optional(),
  // Directional buffers: whether a buffer applies before/after this booking specifically.
  // Optional; missing fields read as buffered (default true) per the directional-buffers spec.
  hasPreBuffer: z.boolean().optional(),
  hasPostBuffer: z.boolean().optional(),
  // Marks the booking as a group session. Extracted at reporting time for manual billing.
  // Defaults to false; existing bookings without the field deserialize as false via Zod default.
  isGroupBooking: z.boolean().optional().default(false),
  gratis: z.boolean().optional().default(false),
  // Bounded series waive: `gratis` is effective only from this date (start of a billing month)
  // onward. Absent = legacy unbounded whole-series waive. See ember occurrenceWaiver.
  gratisFrom: z.string().optional(),
  // Explicit per-occurrence waives. Each names one occurrence (UTC "YYYY-MM-DD") whose fee was
  // removed, with the reason/attribution for the audit trail. Drives the per-row Waive/Restore
  // state in the occurrence lists — must be declared here or the ingestor drops it.
  waivedOccurrences: z
    .array(
      z.object({
        date: z.string(),
        reason: z.string().optional(),
        waivedByMemberId: z.string().optional(),
        waivedAt: z.string().optional(),
      }),
    )
    .optional(),
  comments: CommentSchema.array().optional(),
  bookedAmenities: AmenityBookingSchema.array().optional(),
  wasWarnedAboutUnbookableGap: z.boolean().optional().default(false),
  consecutiveChainMetadata: z
    .object({
      chainIds: z.array(z.string()), // IDs of all bookings in this consecutive chain
      mergedStartTime: z.string(), // Earliest start time in chain (HHmm format)
      mergedEndTime: z.string(), // Latest end time in chain (HHmm format)
      totalDurationHours: z.number(), // Total billable hours for the merged chain (the merged span)
      // Sum of the chain bookings' OWN durations. An unbookable gap exists ONLY when
      // totalDurationHours > bookedDurationHours; when equal the chain is fully contiguous (gap filled)
      // and no "unusable gap" panel should show. Optional — absent on metadata written before this field.
      bookedDurationHours: z.number().optional(),
    })
    .optional(),
  canOverride: z.boolean().optional().default(true), // When true, RND import can overwrite/remove this booking
  // Set when this booking was cancelled by an "End membership" cascade so revoke can find
  // it again and consider it for reinstatement (subject to current availability).
  cancelledByMembershipEnd: z.boolean().optional(),
  // Set when an org-deactivation cascade cancelled this booking (reporting parity with ember).
  cancelledByOrgDeactivation: z.boolean().optional(),
  // Snapshot of the recurrence.until that existed before "End membership" truncated it.
  // `null` means the recurrence was indefinite before the truncation. Used on revoke to
  // restore the original schedule.
  originalRecurrenceUntil: z.union([z.null(), z.coerce.date()]).optional(),
  officeRnd: z
    .object({
      id: z.string(), // OfficeRND's booking ID
      isFree: z.boolean().optional(),
      locationId: z.string().optional(), // OfficeRND location ID
      memberId: z.string().optional(), // OfficeRND member ID
      resourceId: z.string().optional(), // OfficeRND resource ID
      start: z.string().optional(), // OfficeRND start date (ISO string)
      end: z.string().optional(), // OfficeRND end date (ISO string)
      rate: z.string().optional(), // OfficeRND rate ID
      reference: z.string().optional(), // OfficeRND reference code
      source: z.string().optional(), // Provenance marker, e.g. "green-test-import" for the Dev Tools importer
      syncedAt: z.string().optional(), // Our timestamp for when we created/updated this record
      didOverride: z.boolean().optional(), // Whether this booking overrode existing bookings
      overridden: z.array(z.string()).optional(), // Array of MongoDB IDs that were overridden
    })
    .optional(),
  createdAt: z.union([z.string(), z.coerce.string()]), // ISO date string or Date object
  updatedAt: z.union([z.string(), z.coerce.string()]), // ISO date string or Date object
});

export type Booking = z.infer<typeof BookingSchema>;

export interface BookingFilters {
  // ids
  memberId?: string;
  locationId?: string;
  roomId?: string;
  // organisationId expands server-side to every member of the org. Elevated users only.
  organisationId?: string;

  // membership
  type?: MembershipType;

  // date
  dateFrom?: Date;
  dateTo?: Date;
  specificDate?: Date;

  // time
  startTimeAfter?: string; // HH:mm
  endTimeBefore?: string; // HH:mm

  // room colour (Tailwind bg class stored on room.swatch, e.g. "bg-red-500").
  // Server-side filter — narrows the result set pre-pagination.
  swatch?: string;

  // status
  isRecurring?: boolean;
  isCancelled?: boolean;
  isSystemBooking?: boolean;
  hasBuffers?: boolean;
  hasComments?: boolean;
  hasAmenities?: boolean;

  // text search
  searchTerm?: string;
}

export interface BookingSort {
  field: "date" | "startTime";
  order: "asc" | "desc";
}
